import { useMemo, useRef } from "react";
import { getHarExtraFilterEntries, humanizeKey } from "./HarExtraFilters.def";
import { useListingFilterLabels } from "scripts/hooks/useListingFilterLabels";
import "./HarExtraFilters.scss";

export interface IHarExtraFiltersProps {
  /** The passthrough bag (`IFilters.extra`) as it currently stands. */
  extra?: Record<string, string>;
  /** Called with the next bag, or `undefined` once nothing is left applied. */
  onChange: (extra?: Record<string, string>) => void;
}

/**
 * "HAR.com extra filters" — the native filters carried over from the HAR.com
 * search that this panel has no control for (see `HarExtraFilters.def.ts`).
 *
 * They stay applied to every follow-up search, so each one is a closeable pill,
 * the same `filterpill` the school typeaheads render: the "x" drops the key from
 * `filters.extra`, which is what actually removes it from the next search.
 *
 * A dropped filter cannot be picked again from anywhere in the UI — nothing here
 * knows how to build `mohoodid=1573` from scratch — so instead of vanishing it
 * stays as the design system's plain (light) pill, whose "+" puts it back.
 * Renders nothing when there is nothing carried over.
 */
export function HarExtraFilters(props: IHarExtraFiltersProps) {
  const { extra, onChange } = props;

  // The bag as it stood when the modal opened, so a removed pill still has a
  // value to be restored from — and Reset drops the bag wholesale. The modal is
  // mounted fresh each time it opens, making mount the right snapshot point;
  // merging the live bag on top keeps pills for keys added while open.
  const openedWith = useRef(extra ?? {});

  // Labels come from HAR.com's own search form (the hook), so a native key this
  // build has never seen still reads properly; `labelFor` echoes the key back
  // when the form doesn't know it either, and that's where humanizing kicks in.
  const { labelFor } = useListingFilterLabels();

  const entries = useMemo(
    () =>
      getHarExtraFilterEntries({ ...openedWith.current, ...(extra ?? {}) })
        .map((entry) => {
          const label = labelFor(entry.key);
          return {
            ...entry,
            label: label === entry.key ? humanizeKey(entry.key) : label,
          };
        })
        // Sorted by label so the list reads the same way twice.
        .sort((a, b) => a.label.localeCompare(b.label)),
    [extra, labelFor],
  );

  if (!entries.length) return null;

  const toggle = (key: string, value: string, applied: boolean) => {
    const next = { ...(extra ?? {}) };
    if (applied) next[key] = value;
    else delete next[key];
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <div className="px-4 pb-4 har_extra_filters">
      <label
        className="mb-1"
        htmlFor="HarExtraFilters"
        style={{ fontSize: "27px", color: "#000", letterSpacing: "-0.64px" }}
      >
        HAR.com extra filters
      </label>
      <div className="font_size--small color_auxiliary mb-3">
        Carried over from your HAR.com search. Remove any you don't want applied.
      </div>
      <div className="d-flex flex-wrap" id="HarExtraFilters">
        {entries.map(({ key, label, value }) => {
          const title = `${key}=${value}`;

          // Dropped, but still offered back — the plain pill carries the "+".
          if (extra?.[key] == null) {
            return (
              <button
                key={key}
                type="button"
                className="filterpill mr-2 mb-2 cursor--pointer"
                aria-label={`Apply ${label}`}
                onClick={() => toggle(key, value, true)}
              >
                {label}
              </button>
            );
          }

          return (
            <div
              key={key}
              className="filterpill filterpill--closeable mr-2 mb-2"
              title={title}
            >
              <span>{label}</span>
              {/* The "x" icon is drawn by `.filterpill--closeable a`, so it has
                  to be an anchor; role/tabIndex make it reachable anyway. */}
              <a
                className="cursor--pointer"
                role="button"
                tabIndex={0}
                aria-label={`Remove ${label}`}
                onClick={() => toggle(key, value, false)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault();
                  toggle(key, value, false);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
